"use client";

import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { errorMessage, formatAmount, txUrl } from "@/lib/format";
import { feeToPercent, sqrtPriceToPrice, tickToPrice } from "@/domain/v3-math";
import { useConnection, usePublicClient, useWalletClient } from "wagmi";

import { AddToWalletButton } from "@/components/AddToWalletButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TokenAvatar } from "@/components/TokenAvatar";
import { V3_POSITION_MANAGER } from "@/data/contracts/addresses";
import { V3_POSITION_MANAGER_ABI } from "@/data/contracts/abis";
import { useState } from "react";
import { useStatus } from "@/hooks/useStatus";
import { fetchFreshV3Positions, positionsQueryKey, useV3Positions } from "@/hooks/useV3Positions";

// The Remove button leans on Button's `destructive` variant, which renders
// border-transparent by default (see components/ui/button.tsx) — pair it
// with --color-danger-border (already tuned for contrast in both themes,
// see app/globals.css) instead of a flat gray that would wash out in one.
const DESTRUCTIVE_BORDER = "border border-[color:var(--color-danger-border)]";

const PAGE_SIZE = 2;
const MAX_UINT128 = (1n << 128n) - 1n;

function PositionItemSkeleton() {
  return (
    <Card className="bg-muted">
      <CardContent>
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-10" />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-center">
          <div className="space-y-1">
            <Skeleton className="mx-auto h-3 w-8" />
            <Skeleton className="mx-auto h-3 w-12" />
          </div>
          <div className="space-y-1">
            <Skeleton className="mx-auto h-3 w-14" />
            <Skeleton className="mx-auto h-3 w-12" />
          </div>
          <div className="space-y-1">
            <Skeleton className="mx-auto h-3 w-8" />
            <Skeleton className="mx-auto h-3 w-12" />
          </div>
        </div>
        <Skeleton className="mt-2 h-5 w-24 rounded-full" />
        <div className="mt-2 grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-3 w-10" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-10" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-3 w-14" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Skeleton className="h-8 w-28 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
}

export function PositionsList() {
  const { address, isConnected } = useConnection();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { data: rawPositions, isLoading, error } = useV3Positions(address);
  const { showStatus } = useStatus();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [txHashes, setTxHashes] = useState<Record<string, string>>({});

  // A closed position (no liquidity, nothing left to collect) still exists
  // as an NFT on-chain — removing liquidity doesn't burn it — but there's
  // nothing left to do with it here, so drop it from the list instead of
  // showing a dead entry.
  const positions = rawPositions?.filter((p) => p.liquidity > 0n || p.tokensOwed0 > 0n || p.tokensOwed1 > 0n);

  // Bypasses both the server's TTL cache and this query's staleTime, then
  // writes the result straight into the query cache — refetch() alone would
  // just re-serve the same stale server response for the rest of the TTL
  // window right after a write, making the list look unchanged.
  async function refreshPositions() {
    if (!address) return;
    const fresh = await fetchFreshV3Positions(address);
    queryClient.setQueryData(positionsQueryKey(address), fresh);
  }

  const totalPages = Math.max(1, Math.ceil((positions?.length ?? 0) / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const visible = positions?.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  async function collectFees(tokenId: bigint) {
    if (!walletClient || !address || !publicClient) return;
    const id = tokenId.toString();
    setBusyId(id);
    try {
      const hash = await walletClient.writeContract({
        address: V3_POSITION_MANAGER,
        abi: V3_POSITION_MANAGER_ABI,
        functionName: "collect",
        args: [{ tokenId, recipient: address, amount0Max: MAX_UINT128, amount1Max: MAX_UINT128 }],
      });
      setTxHashes((prev) => ({ ...prev, [id]: hash }));
      showStatus("Collecting fees…", "info");
      await publicClient.waitForTransactionReceipt({ hash });
      showStatus("Fees collected!", "success");
      refreshPositions();
    } catch (e) {
      showStatus(errorMessage(e), "error");
    } finally {
      setBusyId(null);
    }
  }

  async function removeLiquidity(tokenId: bigint, liquidity: bigint) {
    if (!walletClient || !address || !publicClient) return;
    const id = tokenId.toString();
    setBusyId(id);
    try {
      const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200);
      const decreaseHash = await walletClient.writeContract({
        address: V3_POSITION_MANAGER,
        abi: V3_POSITION_MANAGER_ABI,
        functionName: "decreaseLiquidity",
        args: [{ tokenId, liquidity, amount0Min: 0n, amount1Min: 0n, deadline }],
      });
      setTxHashes((prev) => ({ ...prev, [id]: decreaseHash }));
      showStatus("Removing liquidity…", "info");
      await publicClient.waitForTransactionReceipt({ hash: decreaseHash });
      const collectHash = await walletClient.writeContract({
        address: V3_POSITION_MANAGER,
        abi: V3_POSITION_MANAGER_ABI,
        functionName: "collect",
        args: [{ tokenId, recipient: address, amount0Max: MAX_UINT128, amount1Max: MAX_UINT128 }],
      });
      setTxHashes((prev) => ({ ...prev, [id]: collectHash }));
      await publicClient.waitForTransactionReceipt({ hash: collectHash });
      showStatus("Liquidity removed!", "success");
      refreshPositions();
    } catch (e) {
      showStatus(errorMessage(e), "error");
    } finally {
      setBusyId(null);
    }
  }

  if (!isConnected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your positions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Connect a wallet to view your V3 positions.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your positions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {isLoading ? (
            <>
              <PositionItemSkeleton />
              <PositionItemSkeleton />
            </>
          ) : error ? (
            <p className="text-sm text-destructive">Failed to load positions.</p>
          ) : positions && positions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No V3 positions found. Add liquidity to create one.</p>
          ) : (
            visible?.map((p, i) => {
              const priceLower = tickToPrice(p.tickLower, p.token0.decimals, p.token1.decimals);
              const priceUpper = tickToPrice(p.tickUpper, p.token0.decimals, p.token1.decimals);
              const currentPrice =
                p.sqrtPriceX96 !== null ? sqrtPriceToPrice(p.sqrtPriceX96, p.token0.decimals, p.token1.decimals) : 0;
              const inRange = p.currentTick !== null && p.currentTick >= p.tickLower && p.currentTick < p.tickUpper;
              const hasFees = p.tokensOwed0 > 0n || p.tokensOwed1 > 0n;
              const busy = busyId === p.tokenId.toString();
              return (
                <Card
                  key={p.tokenId.toString()}
                  className="animate-in bg-muted fade-in-0 slide-in-from-bottom-2 fill-mode-both duration-300 ease-out"
                  style={{ animationDelay: `${Math.min(i, 6) * 60}ms` }}
                >
                  <CardContent>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <TokenAvatar symbol={p.token0.symbol} address={p.token0.address} size={24} />
                        <span className="truncate text-sm font-medium">
                          {p.token0.symbol} / {p.token1.symbol}
                        </span>
                        <Badge variant="outline">{feeToPercent(p.fee)}</Badge>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">#{p.tokenId.toString()}</span>
                    </div>

                    <Badge
                      className={
                        inRange
                          ? "mt-2 border-[color:var(--color-success-border)] bg-[color:var(--color-success-bg)] text-[color:var(--color-in-range)]"
                          : "mt-2 border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] text-[color:var(--color-out-range)]"
                      }
                    >
                      {inRange ? "● IN RANGE" : "● OUT OF RANGE"}
                    </Badge>

                    <div className="mt-3 grid grid-cols-3 gap-2 rounded-[var(--radius-md)] border border-border bg-background/40 p-2 text-center text-xs">
                      <div>
                        <div className="text-muted-foreground">MIN</div>
                        <div className="mt-0.5 font-medium">{priceLower.toFixed(4)}</div>
                      </div>
                      <div className="border-x border-border">
                        <div className="text-muted-foreground">CURRENT</div>
                        <div
                          className={`mt-0.5 font-medium ${inRange ? "text-[color:var(--color-in-range)]" : "text-[color:var(--color-out-range)]"}`}
                        >
                          {currentPrice.toFixed(4)}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">MAX</div>
                        <div className="mt-0.5 font-medium">{priceUpper.toFixed(4)}</div>
                      </div>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>
                        Liquidity
                        <div className="mt-0.5 font-medium text-foreground">{p.liquidity.toString().slice(0, 8)}</div>
                      </div>
                      <div>
                        Fees owed
                        <div className="mt-0.5 font-medium text-foreground">
                          {formatAmount(p.tokensOwed0, p.token0.decimals, 4)} {p.token0.symbol}
                        </div>
                        <div className="font-medium text-foreground">
                          {formatAmount(p.tokensOwed1, p.token1.decimals, 4)} {p.token1.symbol}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                      {hasFees && (
                        <Button size="sm" variant="secondary" disabled={busy} onClick={() => collectFees(p.tokenId)}>
                          💰 Collect fees
                        </Button>
                      )}
                      {p.liquidity > 0n && (
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={busy}
                          className={DESTRUCTIVE_BORDER}
                          onClick={() => removeLiquidity(p.tokenId, p.liquidity)}
                        >
                          📤 Remove
                        </Button>
                      )}
                      <AddToWalletButton
                        asset={{ type: "ERC721", address: V3_POSITION_MANAGER, tokenId: p.tokenId.toString() }}
                        assetLabel={`Position #${p.tokenId}`}
                      />
                    </div>
                    {txHashes[p.tokenId.toString()] && (
                      <a
                        href={txUrl(txHashes[p.tokenId.toString()])}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                      >
                        View transaction ↗
                      </a>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
        {positions && positions.length > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              ← Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              Next →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
